import React from 'react';
import { previewSyncNaver } from '../../api/api';
import type { SyncNaverPreview } from '../../types/syncPreview';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { appModal } from '../../utils/appModal';
import { appMessage as message } from '../../utils/appMessage';
import { formatPrice } from '../../utils/format';
import { formatRate } from '../../utils/syncPreview';
import { currentRequestStore } from '../../utils/decidedPrice';

/** 확인 창이 보는 카드 — 화면이 가진 값(요약을 못 받았을 때 쓴다). */
export interface DecidedConfirmCard {
  id:           number;
  productName:  string | null;
  /** 네이버 가격(기준가). */
  currentPrice: number | null;
}

/**
 * ② 하락 검토 '입력한 금액으로 적용' 확인 창의 요약(감사 card-controltower#692 · 결정 #652, 사장 2026-11-02) — 적용가는 판매가 결정
 * API 로 가서 ③ 을 기다리지 않고 바로 네이버 가격을 바꾸고 PriceHub 에 매장 직접 변경 보고를 보낸다. 그래서 ③ 처럼 미리보기
 * (sync-naver/preview — 네이버 가격 · 확정 대기 · 상승 상한)를 받아 무엇이 바뀌는지 보고 한 번 더 누르게 한다.
 * 미리보기는 지금 수정가 기준이라 '→' 오른쪽은 미리보기가 아니라 입력한 적용가다.
 */
export const DecidedPriceSummary: React.FC<{
  card: DecidedConfirmCard;
  price: number;
  preview: SyncNaverPreview | null;
}> = ({ card, price, preview }) => {
  const t = useThemeTokens();
  const line: React.CSSProperties = { fontSize: FONT_SIZE.sm, color: t.textPrimary, lineHeight: LINE_HEIGHT.normal };
  const box = (bg: string, border: string): React.CSSProperties => ({
    fontSize: FONT_SIZE.sm, color: t.textPrimary, background: bg, border: `1px solid ${border}`, borderRadius: RADIUS.md,
    padding: `${SPACING.xs}px ${SPACING.sm}px`, lineHeight: LINE_HEIGHT.normal,
  });

  // 서버가 본 네이버 가격을 먼저(화면 값이 낡았을 수 있다), 없으면 화면 값.
  const fromServer = preview?.topChanges.find((c) => c.id === card.id)?.currentPrice;
  const base = fromServer ?? card.currentPrice;
  const rate = base != null && base > 0 ? (price - base) / base : null;
  const overCap = preview != null && rate != null && rate > preview.riseCapRate;
  // 한 장이라 건수 없이 갈래 이름만(③ 확인 창의 blockedParts 와 같은 말).
  const parts = preview ? [
    preview.blockedManualOverride > 0 ? '매장 직접 변경 확정 대기' : null,
    preview.blockedHubRequest > 0 ? 'PriceHub 요청 대기' : null,
    preview.blockedHubPending > 0 ? 'PriceHub 확정 대기' : null,
  ].filter((s): s is string => !!s) : [];

  return (
    <div data-testid="decided-preview" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
      <div style={{ ...line, fontWeight: FONT_WEIGHT.semibold, overflowWrap: 'anywhere' }}>
        {card.productName || `#${card.id}`}
      </div>
      <div data-testid="decided-preview-change" style={{ ...line, fontVariantNumeric: 'tabular-nums' }}>
        네이버 가격 {base != null ? formatPrice(base) : '없음'} → 적용가 <b>{formatPrice(price)}</b>
        {rate != null && rate !== 0 && (
          <span style={{ marginLeft: SPACING.xxs, fontWeight: FONT_WEIGHT.semibold, color: rate > 0 ? t.accent : t.danger }}>
            {formatRate(rate)}
          </span>
        )}
      </div>
      <div style={box(t.tintWarning, t.lineWarning)}>
        확인하면 ③ 스토어 반영을 기다리지 않고 <b>바로 네이버 가격을 바꾸고</b> PriceHub 에 매장 직접 변경 보고를 보냅니다(판매가 결정).
        이 가격은 사람이 정한 가격으로 남아 다음 가격 받기가 덮지 않습니다.
      </div>
      {!preview && (
        <div data-testid="decided-preview-missing" style={box(t.tintWarning, t.lineWarning)}>
          반영 요약을 불러오지 못했습니다 — <b style={{ color: t.warning }}>상승 상한 · 확정 대기를 확인하지 못했습니다.</b>
          {' '}금액을 다시 확인한 뒤 반영하세요(적용가 그대로 네이버 가격으로 반영합니다).
        </div>
      )}
      {overCap && (
        <div data-testid="decided-preview-cap" style={box(t.tintWarning, t.lineWarning)}>
          <b style={{ color: t.warning }}>상승률 {Math.round(preview!.riseCapRate * 100)}% 초과</b>
          {' '}— 자동 반영이라면 막히는 크기입니다. 금액을 다시 확인하세요.
        </div>
      )}
      {parts.length > 0 && (
        <div data-testid="decided-preview-blocked" style={box(t.bgRaised, t.border)}>
          이 카드는 {parts.join(' · ')} 중입니다 — ③ 은 건너뛰지만 판매가 결정은 바로 반영 · 보고합니다.
        </div>
      )}
    </div>
  );
};


/** 미리보기 대기 한도 — 응답이 오지 않으면 요약 없이 묻는다(창이 늦게 뜨거나 영영 안 뜨지 않게, 감사 #827). */
export const DECIDED_PREVIEW_TIMEOUT_MS = 10_000;

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('timeout')), ms);
  p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e); });
});

export interface ConfirmDecidedOptions {
  /**
   * 부른 쪽(useInlinePriceEdit)의 생명주기 — 화면이 내려가면(로그아웃 · 세션 만료 · 매장 전환으로 카드 화면 재마운트) abort 해
   * 떠 있는 창을 닫고 false 로 끝낸다(감사 #827 — 예전 모듈 전역 플래그는 창이 안 풀리면 그 탭의 ② 적용을 전부 막았다).
   */
  signal?: AbortSignal;
  /** 창을 연 시점의 매장 — 미리보기를 받은 뒤 매장이 바뀌었으면 그 요약을 보이지 않고 false(감사 #828). */
  store?: string | null;
  /**
   * 창을 연 시점의 요청 매장 헤더(http.ts currentStoreHeader — SUPER 가 고른 매장, 계정 매장이면 null). 주면 미리보기를 그 매장으로 고정해
   * 받는다(storeOverride, 감사 #845 — ③ 확인 창과 같은 방식).
   */
  header?: string | null;
}

/**
 * ② 적용가 확인 — 미리보기를 받아 확인 창을 띄우고 '네이버에 반영' 을 누르면 true. 취소 · 창이 닫힘 · 부른 쪽이 abort · 매장이 바뀜이면
 * false(아무것도 보내지 않는다). 연타 방지는 부른 쪽이 한다(훅의 ref). 미리보기가 실패해도(옛 서버 404 · 오류 · 시간 초과) 요약 없이
 * 묻되, 상승 상한을 확인하지 못했다고 알리고 버튼도 '요약 없이 반영' 으로(감사 #830).
 */
export async function confirmDecidedPrice(
  card: DecidedConfirmCard,
  price: number,
  opts: ConfirmDecidedOptions = {},
): Promise<boolean> {
  const { signal, store } = opts;
  if (signal?.aborted) return false;
  let preview: SyncNaverPreview | null = null;
  message.loading({ key: 'decided-preview', content: '반영할 내용을 확인하는 중…', duration: 0 });
  try {
    preview = await withTimeout(
      'header' in opts ? previewSyncNaver([card.id], { store: opts.header ?? null }) : previewSyncNaver([card.id]),
      DECIDED_PREVIEW_TIMEOUT_MS,
    );
  } catch {
    preview = null;
  }
  if (signal?.aborted) {
    message.open({ key: 'decided-preview', type: 'info', content: '화면이 바뀌어 확인을 멈췄습니다', duration: 2 });
    return false;
  }
  if (store !== undefined && currentRequestStore() !== store) {
    message.open({ key: 'decided-preview', type: 'warning', content: '매장이 바뀌어 적용가를 보내지 않았습니다 — 다시 확인해 주세요', duration: 8 });
    return false;
  }
  message.open({ key: 'decided-preview', type: 'info', content: '반영 전 확인 창을 확인하세요', duration: 1 });
  return new Promise<boolean>((resolve) => {
    let done = false;
    let modal: { destroy?: () => void } | undefined;
    const onAbort = () => { modal?.destroy?.(); finish(false); };
    function finish(v: boolean) {
      if (done) return;
      done = true;
      signal?.removeEventListener('abort', onAbort);
      resolve(v);
    }
    modal = appModal.confirm({
      title: '② 적용 — 네이버 가격을 바로 바꿉니다',
      icon: null,
      width: 520,
      content: <DecidedPriceSummary card={card} price={price} preview={preview} />,
      okText: preview ? '네이버에 반영' : '요약 없이 네이버에 반영',
      cancelText: '취소',
      onOk: () => finish(true),
      onCancel: () => finish(false),
      // 누르지 않고 닫힌 경우(destroy · 다른 길)도 끝낸다 — 약속이 안 풀린 채 남지 않게.
      afterClose: () => finish(false),
    }) as { destroy?: () => void } | undefined;
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) onAbort();
  });
}